import { useState, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useConfirm } from '../../hooks/useConfirm';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';
import Textarea from '../../components/ui/Textarea';
import DataTable, { Column, DataTableFetchParams, DataTableFilterDef } from '../../components/ui/DataTable';
import { marketplaceService, ListingCounts, CvVerification } from '../../services/marketplace.service';
import { formConfigService } from '../../services/form-config.service';
import { Listing, ListingDetail, CharterDetails } from '../../interfaces/listing.interface';
import { FormConfig, FormFieldDef, FormType, InputType } from '../../interfaces/form-config.interface';
import { FORM_TYPE_LABELS } from '../../interfaces/form-config.interface';
import { getErrorMsg } from '../../utils/error';
import { STATUS_COLORS, LISTING_FORM_TYPE_OPTIONS } from './listings.constants';
import { Permission } from '../../permissions/permission.enum';
import { useAuth } from '../../context/AuthContext';
import YachtWorldImportDialog from './YachtWorldImportDialog';
import ImportTasksTable from './ImportTasksTable';

// ─── Field value renderers ────────────────────────────────────────────────────

const IMAGE_INPUT_TYPES = new Set<InputType>([
  InputType.IMAGE_UPLOADER,
  InputType.IMAGE_VIDEO_UPLOADER,
]);

/** Look up the English display label for a field's submitted value (option-based fields). */
function resolveOptionLabel(field: FormFieldDef, value: unknown): string {
  if (!field.options?.length || typeof value !== 'string') return String(value ?? '');
  const opt = field.options.find((o) => o.value === value);
  return opt?.translations?.['en'] ?? opt?.label ?? String(value);
}

/** Render a single field value according to its inputType. */
function FieldValue({ field, value }: { field: FormFieldDef; value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 italic text-xs">—</span>;
  }

  // Option-based single value
  if (
    field.inputType === InputType.DROPDOWN ||
    field.inputType === InputType.SELECTABLE_PILL
  ) {
    return <span className="text-gray-700 dark:text-gray-300">{resolveOptionLabel(field, value)}</span>;
  }

  // Option-based multi value
  if (
    field.inputType === InputType.MULTI_SELECTABLE_PILL ||
    field.inputType === InputType.MULTI_DROPDOWN
  ) {
    const values = Array.isArray(value) ? value : [value];
    return (
      <div className="flex flex-wrap gap-1">
        {values.map((v, i) => (
          <span
            key={i}
            className="px-2 py-0.5 rounded-full text-xs bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800"
          >
            {resolveOptionLabel(field, v)}
          </span>
        ))}
      </div>
    );
  }

  // Toggle
  if (field.inputType === InputType.TOGGLE || field.inputType === InputType.TOGGLE_SPECIAL) {
    return (
      <span className={value ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
        {value ? 'Yes' : 'No'}
      </span>
    );
  }

  // Spec dynamic — array of { key, value } pairs
  if (field.inputType === InputType.SPEC_DYNAMIC) {
    const pairs = Array.isArray(value) ? value as Array<{ key?: string; value?: string }> : [];
    if (!pairs.length) return <span className="text-gray-400 italic text-xs">—</span>;
    return (
      <div className="space-y-0.5">
        {pairs.map((pair, i) => (
          <div key={i} className="flex gap-1.5 text-xs">
            <span className="font-medium text-gray-600 dark:text-gray-400 shrink-0">{pair.key || '—'}:</span>
            <span className="text-gray-700 dark:text-gray-300">{pair.value || '—'}</span>
          </div>
        ))}
      </div>
    );
  }

  // Image array (fallback — image fields are excluded from the section normally)
  if (Array.isArray(value)) {
    const isImageArr = typeof value[0] === 'string' && /\.(jpg|jpeg|png|webp|gif)/i.test(String(value[0]));
    if (isImageArr) {
      return (
        <div className="flex flex-wrap gap-1">
          {(value as string[]).map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer">
              <img
                src={url}
                alt={`img-${i}`}
                className="w-14 h-14 object-cover rounded border border-gray-200 dark:border-gray-700 hover:opacity-80"
              />
            </a>
          ))}
        </div>
      );
    }
    // Any other array of scalars (e.g. fuel_type, which is stored as an array
    // whatever its configured input type) — show the values, not a count.
    // Arrays of objects have no sensible one-line form, so they keep the count.
    if (value.some((v) => v !== null && typeof v === 'object')) {
      return <span className="text-gray-700 dark:text-gray-300 text-xs">[{value.length} items]</span>;
    }
    return (
      <span className="text-gray-700 dark:text-gray-300">
        {(value as unknown[]).map((v) => resolveOptionLabel(field, v)).join(', ')}
      </span>
    );
  }

  // Long text
  const str = String(value);
  if (str.length > 100) {
    return (
      <span className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
        {str}
      </span>
    );
  }

  return <span className="text-gray-700 dark:text-gray-300">{str}</span>;
}

// ─── Add-on (paid promotion) helpers ──────────────────────────────────────────

/** Format an add-on expiry timestamp for display, or null when absent. */
function formatUntil(value?: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

/**
 * An add-on counts as active only when its flag is set AND its expiry is a
 * valid date still in the future — mirrors the Mobile API rule
 * (`isBoosted = true AND boostedUntil > NOW()`). A set flag with a missing or
 * expired "until" date is NOT active, so the CMS never shows a stale chip.
 */
function isAddonActive(flag: boolean, until?: string): boolean {
  if (!flag || !until) return false;
  const d = new Date(until);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
}

const ADDON_CHIP_BASE = 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold';

/** Compact chips for the list view — only renders add-ons that are active (flag set AND not expired). */
function AddonChips({ listing }: { listing: Listing }) {
  const { t } = useTranslation('common');
  const boostActive = isAddonActive(listing.isBoosted, listing.boostedUntil);
  const topActive = isAddonActive(listing.isTopOfPage, listing.topOfPageUntil);
  const whatsappActive = isAddonActive(listing.hasWhatsapp, listing.whatsappUntil);
  const popupAdActive = isAddonActive(listing.hasPopupAd ?? false, listing.popupAdUntil ?? undefined);
  if (!boostActive && !topActive && !whatsappActive && !popupAdActive) {
    return <span className="text-gray-300 dark:text-gray-600">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {boostActive && (
        <span className={`${ADDON_CHIP_BASE} bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300`}>
          ⚡ Boost
        </span>
      )}
      {topActive && (
        <span className={`${ADDON_CHIP_BASE} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300`}>
          ↑ Top
        </span>
      )}
      {whatsappActive && (
        <span className={`${ADDON_CHIP_BASE} bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300`}>
          WhatsApp
        </span>
      )}
      {popupAdActive && (
        <span className={`${ADDON_CHIP_BASE} bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300`}>
          {t('addon_popup_ad')}
        </span>
      )}
    </div>
  );
}

/** Detail-view rows: each active add-on (flag set AND not expired) with its "active until" date. */
function AddonDetailRows({ listing }: { listing: Listing }) {
  const { t } = useTranslation('common');
  const whatsappActive = isAddonActive(listing.hasWhatsapp, listing.whatsappUntil);
  const rows: { label: string; until: string | null }[] = [];
  if (isAddonActive(listing.isBoosted, listing.boostedUntil)) rows.push({ label: '⚡ Boosted', until: formatUntil(listing.boostedUntil) });
  if (isAddonActive(listing.isTopOfPage, listing.topOfPageUntil)) rows.push({ label: '↑ Top of Page', until: formatUntil(listing.topOfPageUntil) });
  if (whatsappActive) rows.push({ label: 'WhatsApp', until: formatUntil(listing.whatsappUntil) });
  if (isAddonActive(listing.hasPopupAd ?? false, listing.popupAdUntil ?? undefined)) rows.push({ label: t('addon_popup_ad'), until: formatUntil(listing.popupAdUntil ?? undefined) });
  if (rows.length === 0) return null;
  return (
    <div className="px-5 py-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
        Add-ons
      </p>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-sm">
            <span className={`${ADDON_CHIP_BASE} bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300`}>
              {r.label}
            </span>
            <span className="text-gray-500 dark:text-gray-400 text-xs">
              {r.until ? `until ${r.until}` : 'no expiry set'}
            </span>
          </div>
        ))}
        {whatsappActive && listing.whatsapp && (
          <div className="flex items-center justify-between text-sm pt-1">
            <span className="text-gray-500 dark:text-gray-400 text-xs">WhatsApp number</span>
            <span className="text-gray-700 dark:text-gray-300">{listing.whatsapp}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Listing Detail Drawer ────────────────────────────────────────────────────

interface DetailDrawerProps {
  listingId: string | null;
  onClose: () => void;
  onApprove: (id: string) => Promise<boolean>;
  onOpenReject: (id: string) => void;
}

const DetailDrawer = ({ listingId, onClose, onApprove, onOpenReject }: DetailDrawerProps) => {
  const { t } = useTranslation('common');
  const [detail, setDetail] = useState<ListingDetail | null>(null);
  const [formConfig, setFormConfig] = useState<FormConfig | null>(null);
  const [cvVerification, setCvVerification] = useState<CvVerification | null>(null);
  const [charterDetails, setCharterDetails] = useState<CharterDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!listingId) { setDetail(null); setFormConfig(null); setCvVerification(null); setCharterDetails(null); return; }
    setLoading(true);
    setError('');
    marketplaceService.getListing(listingId)
      .then(async (d) => {
        setDetail(d);
        const tasks: Promise<void>[] = [];
        tasks.push(
          formConfigService.getByType(d.formType as FormType)
            .then(setFormConfig)
            .catch(() => {}),
        );
        if (d.formType === FormType.SKILL_LABOUR) {
          tasks.push(
            marketplaceService.getCvVerification(listingId)
              .then(setCvVerification)
              .catch(() => {}),
          );
        }
        if (d.formType === FormType.CHARTER) {
          tasks.push(
            marketplaceService.getCharterDetails(listingId)
              .then(setCharterDetails)
              .catch(() => {}),
          );
        }
        await Promise.all(tasks);
      })
      .catch((err) => setError(getErrorMsg(err)))
      .finally(() => setLoading(false));
  }, [listingId]);

  const handleApprove = async () => {
    if (!detail) return;
    setApproving(true);
    try {
      const approved = await onApprove(detail.id);
      if (approved) setDetail((prev) => prev ? { ...prev, status: 'active' } : prev);
    } finally {
      setApproving(false);
    }
  };

  if (!listingId) return null;

  // ── Build step-grouped field list from form config ───────────────────────
  type FieldSubGroup = { groupName: string | null; fields: FormFieldDef[] };
  type StepGroup = { stepNum: number; label: string; subGroups: FieldSubGroup[] };
  const stepGroups: StepGroup[] = [];

  if (formConfig && detail?.data) {
    const stepLabelMap = new Map(
      formConfig.steps.map((s) => [s.stepNumber, s.translations?.['en'] ?? `Step ${s.stepNumber}`]),
    );

    const activeFields = formConfig.fields
      .filter((f) => f.isActive !== false && f.visible && !IMAGE_INPUT_TYPES.has(f.inputType as InputType))
      .sort((a, b) => a.step - b.step || a.order - b.order);

    const byStep = new Map<number, FormFieldDef[]>();
    for (const field of activeFields) {
      if (!byStep.has(field.step)) byStep.set(field.step, []);
      byStep.get(field.step)!.push(field);
    }

    for (const [stepNum, fields] of Array.from(byStep.entries())) {
      // Only include fields that have a submitted value
      const fieldsWithData = fields.filter(
        (f) => detail.data![f.key] !== undefined && detail.data![f.key] !== null && detail.data![f.key] !== '',
      );
      if (fieldsWithData.length === 0) continue;

      // Sub-group by field.group, preserving order
      const subGroups: FieldSubGroup[] = [];
      let currentGroup: FieldSubGroup | null = null;
      for (const field of fieldsWithData) {
        const gName = field.group ?? null;
        if (currentGroup && currentGroup.groupName === gName) {
          currentGroup.fields.push(field);
        } else {
          currentGroup = { groupName: gName, fields: [field] };
          subGroups.push(currentGroup);
        }
      }

      stepGroups.push({
        stepNum,
        label: stepLabelMap.get(stepNum) ?? `Step ${stepNum}`,
        subGroups,
      });
    }
  }

  // Fields in data that aren't covered by the form config (orphaned / legacy data)
  const configuredKeys = new Set(formConfig?.fields.map((f) => f.key) ?? []);
  const entityKeys = new Set(['photos_media', 'video_url', 'videoUrl']);
  const orphanEntries = detail?.data
    ? Object.entries(detail.data).filter(
        ([k]) => !configuredKeys.has(k) && !entityKeys.has(k),
      )
    : [];

  const images: string[] = Array.isArray(detail?.images) ? detail!.images : [];

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            {loading ? (
              <div className="h-5 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            ) : (
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                {detail?.title ?? t('listing_detail')}
              </h2>
            )}
            {detail && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {FORM_TYPE_LABELS[detail.formType as keyof typeof FORM_TYPE_LABELS] ?? detail.formType}
                </span>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${STATUS_COLORS[detail.status] ?? ''}`}>
                  {detail.status}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Action bar — pending only */}
        {detail?.status === 'pending' && (
          <div className="px-5 py-3 bg-yellow-50 dark:bg-yellow-950/30 border-b border-yellow-100 dark:border-yellow-900/40 flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-yellow-700 dark:text-yellow-400 flex-1">
              This listing is awaiting review.
            </span>
            <Button size="sm" variant="primary" onClick={handleApprove} disabled={approving}>
              {approving ? 'Approving…' : t('approve')}
            </Button>
            <Button size="sm" variant="danger" onClick={() => onOpenReject(detail.id)}>
              {t('reject')}
            </Button>
          </div>
        )}

        {/* Rejection reason banner */}
        {detail?.rejectionReason && (
          <div className="px-5 py-3 bg-red-50 dark:bg-red-950/30 border-b border-red-100 dark:border-red-900/40 flex-shrink-0">
            <p className="text-xs text-red-700 dark:text-red-400">
              <span className="font-semibold">Rejection reason: </span>
              {detail.rejectionReason}
            </p>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading && (
            <div className="p-6 space-y-4">
              {[80, 60, 90, 50, 70].map((w, i) => (
                <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" style={{ width: `${w}%` }} />
              ))}
            </div>
          )}

          {error && (
            <div className="p-6">
              <div className="p-3 rounded bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/40">
                {error}
              </div>
            </div>
          )}

          {!loading && !error && detail && (
            <div className="divide-y divide-gray-100 dark:divide-gray-800 pb-8">

              {/* Image gallery */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                  Photos {images.length > 0 && `(${images.length})`}
                </p>
                {images.length > 0 ? (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {images.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                        <img
                          src={url}
                          alt={`photo-${i + 1}`}
                          className="w-32 h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700 hover:opacity-90 transition-opacity"
                        />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">{t('no_images')}</p>
                )}
                {detail.videoUrl && (
                  <a
                    href={detail.videoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('video')}
                  </a>
                )}
              </div>

              {/* CV / Resume — shown for SKILL_LABOUR listings */}
              {(() => {
                const raw = detail.data?.['cv_upload'];
                const dataUrl = Array.isArray(raw) ? (raw as string[])[0] : typeof raw === 'string' ? raw : null;
                const cvUrl = cvVerification?.cvUrl ?? dataUrl;
                if (!cvUrl && !cvVerification) return null;

                const statusColors: Record<string, string> = {
                  pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
                  approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
                  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
                };

                return (
                  <div className="px-5 py-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                      CV / Resume
                    </p>
                    <div className="space-y-2">
                      {cvUrl && (
                        <a
                          href={cvUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          View CV ↗
                        </a>
                      )}
                      {cvVerification && (
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                          <span className={`px-2 py-0.5 rounded-full font-semibold ${statusColors[cvVerification.status] ?? ''}`}>
                            {cvVerification.status}
                          </span>
                          {cvVerification.reviewedAt && (
                            <span>Reviewed {new Date(cvVerification.reviewedAt).toLocaleDateString()}</span>
                          )}
                          {cvVerification.rejectionReason && (
                            <span className="text-red-500 dark:text-red-400">Reason: {cvVerification.rejectionReason}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Core info grid */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                  Details
                </p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{t('price')}</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                      {detail.contactForPrice
                        ? <span className="text-gray-400 italic text-sm">{t('on_request')}</span>
                        : detail.price != null
                        ? `${detail.currency ?? ''} ${Number(detail.price).toLocaleString()}`
                        : '—'}
                    </p>
                  </div>
                  {detail.discountedPrice != null && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Discounted Price</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                        {detail.currency ?? ''} {Number(detail.discountedPrice).toLocaleString()}
                      </p>
                    </div>
                  )}
                  {detail.location && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400 text-xs">{t('location')}</span>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{detail.location}</p>
                    </div>
                  )}
                  {detail.pickupPoints && detail.pickupPoints.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500 dark:text-gray-400 text-xs">Pickup Points</span>
                      <ul className="mt-0.5 space-y-0.5">
                        {detail.pickupPoints.map((pt, i) => (
                          <li key={i} className="font-medium text-gray-900 dark:text-gray-100 text-sm">{pt}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{t('quality')}</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${detail.qualityScore}%` }} />
                      </div>
                      <span className="text-xs text-gray-500">{detail.qualityScore}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{t('views')}</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{(detail.views ?? 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{t('submitted')}</span>
                    <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                      {new Date(detail.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-500 dark:text-gray-400 text-xs">{t('listing_id')}</span>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-400 mt-0.5 break-all">{detail.id}</p>
                  </div>
                </div>
              </div>

              {/* Paid add-ons (boost / top of page / whatsapp) with expiry */}
              <AddonDetailRows listing={detail} />

              {/* Seller info */}
              <div className="px-5 py-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                  {t('seller_info')}
                </p>
                {detail.seller ? (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                        {(detail.seller.name ?? 'U')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="text-sm space-y-0.5">
                      <p className="font-medium text-gray-900 dark:text-gray-100">{detail.seller.name ?? '—'}</p>
                      {detail.seller.email && <p className="text-gray-500 dark:text-gray-400">{detail.seller.email}</p>}
                      {detail.seller.phone && <p className="text-gray-500 dark:text-gray-400">{detail.seller.phone}</p>}
                      <p className="font-mono text-xs text-gray-400 pt-0.5">{detail.sellerId}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 font-mono">{detail.sellerId}</p>
                )}
              </div>

              {/* Form data — config-driven step groups */}
              {(stepGroups.length > 0 || orphanEntries.length > 0) && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-4">
                    {t('form_data')}
                  </p>

                  <div className="space-y-5">
                    {/* Config-driven steps */}
                    {stepGroups.map((group) => (
                      <div key={group.stepNum}>
                        {stepGroups.length > 1 && (
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 pb-1 border-b border-gray-100 dark:border-gray-800">
                            {group.label}
                          </p>
                        )}
                        <div className="space-y-2.5">
                          {group.subGroups.map((subGroup, si) =>
                            subGroup.groupName ? (
                              <div
                                key={subGroup.groupName}
                                className="rounded-md border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 px-3 py-2.5"
                              >
                                <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-2">
                                  {subGroup.groupName}
                                </p>
                                <div className="space-y-2">
                                  {subGroup.fields.map((field) => (
                                    <div key={field.key} className="grid grid-cols-5 gap-2 text-sm">
                                      <span className="col-span-2 text-gray-500 dark:text-gray-400 text-xs leading-5 pt-0.5">
                                        {field.label}
                                      </span>
                                      <div className="col-span-3">
                                        <FieldValue field={field} value={detail.data![field.key]} />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div key={`ungrouped-${si}`} className="space-y-2.5">
                                {subGroup.fields.map((field) => (
                                  <div key={field.key} className="grid grid-cols-5 gap-2 text-sm">
                                    <span className="col-span-2 text-gray-500 dark:text-gray-400 text-xs leading-5 pt-0.5">
                                      {field.label}
                                    </span>
                                    <div className="col-span-3">
                                      <FieldValue field={field} value={detail.data![field.key]} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Orphaned keys not covered by form config */}
                    {orphanEntries.length > 0 && (
                      <div>
                        {stepGroups.length > 0 && (
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 pb-1 border-b border-gray-100 dark:border-gray-800">
                            Other
                          </p>
                        )}
                        <div className="space-y-2.5">
                          {orphanEntries.map(([key, value]) => (
                            <div key={key} className="grid grid-cols-5 gap-2 text-sm">
                              <span className="col-span-2 text-gray-500 dark:text-gray-400 text-xs leading-5 pt-0.5 font-mono">
                                {key}
                              </span>
                              <div className="col-span-3 text-gray-700 dark:text-gray-300 break-all text-xs">
                                {String(value ?? '—')}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Charter Promotions */}
              {charterDetails && charterDetails.promotions.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                    Promotion Periods ({charterDetails.promotions.length})
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
                        <th className="text-left pb-2 font-medium">Min Days</th>
                        <th className="text-left pb-2 font-medium">Discount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-800/60">
                      {charterDetails.promotions.map((p) => (
                        <tr key={p.id}>
                          <td className="py-2 text-gray-700 dark:text-gray-300">{p.minDays}+ days</td>
                          <td className="py-2 font-medium text-green-600 dark:text-green-400">{Number(p.discountPercent)}% off</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Charter Optional Extras */}
              {charterDetails && charterDetails.optionalExtras.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                    Optional Extras ({charterDetails.optionalExtras.length})
                  </p>
                  <div className="space-y-2">
                    {charterDetails.optionalExtras.map((e) => (
                      <div key={e.id} className="rounded-md border border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 px-3 py-2.5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{e.name}</span>
                              {!e.isActive && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500">
                                  inactive
                                </span>
                              )}
                            </div>
                            {e.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{e.description}</p>
                            )}
                          </div>
                          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex-shrink-0">
                            {detail?.currency ?? ''} {Number(e.price).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Charter Price Rules — date-range pricing & unavailable periods */}
              {charterDetails && charterDetails.priceRules.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3">
                    Price Rules ({charterDetails.priceRules.length})
                  </p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
                        <th className="text-left pb-2 font-medium">Period</th>
                        <th className="text-right pb-2 font-medium">Price / Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-gray-800/60">
                      {charterDetails.priceRules.map((r) => (
                        <tr key={r.id}>
                          <td className="py-2 text-gray-700 dark:text-gray-300">
                            {new Date(r.startDate).toLocaleDateString()} – {new Date(r.endDate).toLocaleDateString()}
                          </td>
                          <td className="py-2 text-right font-medium">
                            {r.isUnavailable ? (
                              <span className="text-red-500 dark:text-red-400">Unavailable</span>
                            ) : r.price != null ? (
                              <span className="text-gray-700 dark:text-gray-200">
                                {detail?.currency ?? ''} {Number(r.price).toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-gray-400 italic">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ListingsPage = () => {
  const { t } = useTranslation('common');
  const { confirm, ConfirmDialog } = useConfirm();
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const { hasPermission } = useAuth();
  const canImport = hasPermission(Permission.LISTINGS_IMPORT);
  const canReadTasks = hasPermission(Permission.BACKGROUND_TASKS_READ);
  const [ywImportOpen, setYwImportOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'active' | 'rejected' | 'tasks'>('active');
  const [counts, setCounts] = useState<ListingCounts>({ pending: 0, active: 0, rejected: 0 });

  useEffect(() => {
    marketplaceService.getListingCounts().then(setCounts).catch(() => {});
  }, [tableRefreshKey]);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const [detailId, setDetailId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const linkedId = searchParams.get('listingId');
    if (linkedId) {
      setDetailId(linkedId);
      setSearchParams((p) => { p.delete('listingId'); return p; }, { replace: true });
    }
  }, []);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const response = await marketplaceService.getListings({
      status: activeTab,
      formType: params.filters?.formType || undefined,
      search: params.search || undefined,
      page: params.page,
      take: params.pageSize,
    });
    return {
      data: response.data,
      total: response.meta.itemCount,
      totalPages: response.meta.pageCount,
    };
  }, [activeTab]);

  const handleApprove = useCallback(async (id: string): Promise<boolean> => {
    if (!await confirm(t('confirm_approve_listing'))) return false;
    setActionError('');
    try {
      await marketplaceService.approveListing(id);
      setTableRefreshKey((k) => k + 1);
      return true;
    } catch (err) {
      setActionError(getErrorMsg(err));
      return false;
    }
  }, [confirm, t]);

  const openReject = (id: string) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectError('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      setRejectError('Rejection reason is required');
      return;
    }
    try {
      await marketplaceService.rejectListing(rejectingId!, rejectReason.trim());
      setRejectDialogOpen(false);
      setRejectingId(null);
      setTableRefreshKey((k) => k + 1);
    } catch (err) {
      setRejectError(getErrorMsg(err));
    }
  };

  const columns = useMemo<Column<Listing>[]>(() => [
    {
      key: 'formType',
      header: t('form_type'),
      render: (l) => (
        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          {FORM_TYPE_LABELS[l.formType as keyof typeof FORM_TYPE_LABELS] ?? l.formType}
        </span>
      ),
    },
    {
      key: 'title',
      header: t('title'),
      sortable: true,
      render: (l) => (
        <button
          className="text-left font-medium text-blue-600 dark:text-blue-400 hover:underline"
          onClick={() => setDetailId(l.id)}
        >
          {l.title}
        </button>
      ),
    },
    {
      key: 'price',
      header: t('price'),
      render: (l) =>
        l.contactForPrice
          ? <span className="text-gray-400 text-sm">{t('on_request')}</span>
          : l.price != null
          ? <span>{l.currency} {Number(l.price).toLocaleString()}</span>
          : '—',
    },
    {
      key: 'status',
      header: t('status'),
      render: (l) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[l.status] ?? ''}`}>
          {l.status}
        </span>
      ),
    },
    {
      key: 'addons',
      header: 'Add-ons',
      render: (l) => <AddonChips listing={l} />,
    },
    {
      key: 'qualityScore',
      header: t('quality_score'),
      render: (l) => (
        <div className="flex items-center gap-1">
          <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${l.qualityScore}%` }} />
          </div>
          <span className="text-xs text-gray-500">{l.qualityScore}</span>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('submitted'),
      sortable: true,
      render: (l) => new Date(l.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: t('actions'),
      className: 'text-right',
      render: (l) => (
        <div className="flex justify-end gap-2">
          {l.status === 'pending' && (
            <>
              <Button size="sm" variant="primary" onClick={() => handleApprove(l.id)}>
                {t('approve')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => openReject(l.id)}>
                {t('reject')}
              </Button>
            </>
          )}
          <Button size="sm" variant="secondary" onClick={() => setDetailId(l.id)}>
            View
          </Button>
        </div>
      ),
    },
  ], [t, handleApprove, openReject]);

  const filterDefs = useMemo<DataTableFilterDef[]>(() => [
    {
      key: 'formType',
      label: t('form_type'),
      type: 'select',
      options: LISTING_FORM_TYPE_OPTIONS,
    },
  ], [t]);

  return (
    <Layout title={t('listings')}>
      <div className="space-y-6 h-full flex flex-col">
        {/* Card + tabs share one container so space-y-6 doesn't insert a gap between them */}
        <div className="flex-shrink-0">
        <Card className="p-4 shadow-sm rounded-b-none border-b-0">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold tracking-tight">{t('listings')}</h1>
            {canImport && (
              <Button variant="secondary" onClick={() => setYwImportOpen(true)}>
                {t('yw_import_button')}
              </Button>
            )}
          </div>
          {actionError && (
            <div className="mt-2 p-2 rounded bg-red-50 text-red-600 text-sm border border-red-100">
              {actionError}
            </div>
          )}
        </Card>

        {/* Status tabs — flush below card header, same border/bg */}
        <div className="flex border border-t-0 border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg shadow-sm">
          {([
            {
              key: 'pending' as const,
              labelKey: 'pending_tab',
              count: counts.pending,
              badgeClass: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
            },
            {
              key: 'active' as const,
              labelKey: 'active_tab',
              count: counts.active,
              badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
            },
            {
              key: 'rejected' as const,
              labelKey: 'rejected_tab',
              count: counts.rejected,
              badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
            },
            // No count: the import-task list has no cheap counter endpoint and
            // a number here would mean nothing to the admin.
            ...(canReadTasks
              ? [{
                  key: 'tasks' as const,
                  labelKey: 'yw_tasks_tab',
                  count: null,
                  badgeClass: '',
                }]
              : []),
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setTableRefreshKey((k) => k + 1);
              }}
              className={[
                'flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              ].join(' ')}
            >
              {t(tab.labelKey)}
              {tab.count !== null && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tab.badgeClass}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        </div>

        {activeTab === 'tasks' ? (
          <ImportTasksTable />
        ) : (
          <DataTable
            className="flex-1 min-h-0"
            columns={columns}
            onFetch={handleFetch}
            filterDefs={filterDefs}
            initialState={{ sortBy: 'createdAt', sortOrder: 'DESC', pageSize: 20 }}
            refreshKey={tableRefreshKey}
            emptyMessage="No listings found"
          />
        )}

        {/* Reject dialog */}
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} title={t('reject_listing')}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">{t('reject_listing_prompt')}</p>
            <Textarea
              label={t('rejection_reason')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Provide a reason for the seller..."
              error={rejectError}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setRejectDialogOpen(false)}>{t('cancel')}</Button>
              <Button variant="danger" onClick={handleRejectConfirm}>{t('reject')}</Button>
            </div>
          </div>
        </Dialog>

        <YachtWorldImportDialog
          open={ywImportOpen}
          onClose={() => setYwImportOpen(false)}
          onImported={() => {
            // Land on the task list — the import runs in the background, so the
            // listings table has nothing new to show yet. Only for an admin who
            // can read tasks: the tab is hidden without that permission, so
            // switching would strand them on a view with no way back.
            if (canReadTasks) setActiveTab('tasks');
          }}
        />

        {/* Detail drawer */}
        <DetailDrawer
          listingId={detailId}
          onClose={() => setDetailId(null)}
          onApprove={handleApprove}
          onOpenReject={(id) => { setDetailId(null); openReject(id); }}
        />
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default ListingsPage;
